import React from 'react';
import uuid from 'react-uuid';
import printJS from 'print-js';
import moment from 'moment';

import { downloadFile, printFile } from '../components/views/_shared/FileViewer';

import api from '../api';
import * as AccessTypes from '../constants/accessTypes';
import * as fn from './_functions';
import * as ach from './accessHelper';
import * as bh from './badgeHelper';
import * as dg from './dx/dataGridHelper';

export const PRINT_MESSAGE = <>
    <h2 className='text-uppercase text-center font-weight-bold text-gray-900 mb-1'>Generating...</h2>
    <p className='fs-xl'>Please wait while we generate the invoice.</p>
</>;

export const isEndState = purchase => {
    return purchase && ['Completed', 'Refunded'].some(s => s === purchase.status) && !purchase.isNoCharge
}

export const getLatestInvoiceQuoteAndPaymentReceipt = (purchase, isGeneratingPrintCallback, reprint = false) => {
    return new Promise((resolve) => {
        if (purchase) {
            if (purchase.payments && purchase.payments.length > 0) {
                const paymentReceiptOption = {
                    documentType: 'PaymentReceipt',
                    customerId: purchase.customer.id,
                    referenceId1: purchase.id,
                    referenceId2: purchase.transactions[purchase.transactions.length - 1].id,
                    referenceId3: purchase.lastPayment ? purchase.lastPayment.id : null,
                    referenceDateTimeUtc: purchase.lastPayment ? moment(purchase.lastPaymentDate).utc() : null,
                };

                api.PrintedDocuments.search({
                    parameters: [
                        { field: 'Type', value: paymentReceiptOption.documentType },
                        { field: 'ReferenceId1', value: paymentReceiptOption.referenceId1 },
                        { field: 'ReferenceId2', value: paymentReceiptOption.referenceId2 },
                        { field: 'ReferenceId3', value: paymentReceiptOption.referenceId3 },
                        { field: 'ReferenceDateTimeUtc', value: paymentReceiptOption.referenceDateTimeUtc },
                        { field: 'DeactivatedDateUtc', value: null },
                    ],
                    sortByFields: [{ field: 'CreatedDateUtc', direction: 'DESC' }],
                    offset: 0, limit: 1, includeTotalCount: false,
                })
                    .then(({ data }) => {
                        let paymentDocument = data.result && data.result.length > 0 ? data.result[0] : null;

                        if (!paymentDocument || reprint) {
                            if (isGeneratingPrintCallback) {
                                isGeneratingPrintCallback(true);
                            }

                            api.PrintedDocuments.print(paymentReceiptOption)
                                .then(({ data }) => {
                                    api.PrintedDocuments.get(data.id)
                                        .then(({ data: printedDocumentData }) => {
                                            if (isGeneratingPrintCallback) {
                                                isGeneratingPrintCallback(false);
                                            }
                                            paymentDocument = printedDocumentData;
                                            resolve(paymentDocument);
                                        })
                                })
                        } else {
                            resolve(paymentDocument);
                        }
                    })
            } else if (purchase.isQuote) {
                const quoteOption = {
                    documentType: 'Quote',
                    customerId: purchase.customer ? purchase.customer.id : null,
                    referenceId1: purchase.id,
                    referenceId2: purchase.transactions[purchase.transactions.length - 1].id,
                    referenceDateTimeUtc: purchase.lastAnyUpdatedDateUtc,
                };

                api.PrintedDocuments.search({
                    parameters: [
                        { field: 'Type', value: quoteOption.documentType },
                        { field: 'ReferenceId1', value: quoteOption.referenceId1 },
                        { field: 'ReferenceId2', value: quoteOption.referenceId2 },
                        { field: 'ReferenceDateTimeUtc', value: quoteOption.referenceDateTimeUtc },
                        { field: 'DeactivatedDateUtc', value: null },
                    ],
                    sortByFields: [{ field: 'CreatedDateUtc', direction: 'DESC' }],
                    offset: 0, limit: 1, includeTotalCount: false,
                })
                    .then(({ data }) => {
                        let quoteDocument = data.result && data.result.length > 0 ? data.result[0] : null;

                        if (!quoteDocument || reprint) {
                            if (isGeneratingPrintCallback) {
                                isGeneratingPrintCallback(true);
                            }

                            api.PrintedDocuments.print(quoteOption)
                                .then(({ data }) => {
                                    api.PrintedDocuments.get(data.id)
                                        .then(({ data: printedDocumentData }) => {
                                            if (isGeneratingPrintCallback) {
                                                isGeneratingPrintCallback(false);
                                            }
                                            quoteDocument = printedDocumentData;
                                            resolve(quoteDocument);
                                        })
                                })
                        } else {
                            resolve(quoteDocument);
                        }
                    })
            } else {
                const invoiceOption = {
                    documentType: 'Invoice',
                    customerId: purchase.customer ? purchase.customer.id : null,
                    referenceId1: purchase.id,
                    referenceId2: purchase.transactions[purchase.transactions.length - 1].id,
                    referenceDateTimeUtc: purchase.lastAnyUpdatedDateUtc,
                };

                api.PrintedDocuments.search({
                    parameters: [
                        { field: 'Type', value: invoiceOption.documentType },
                        { field: 'ReferenceId1', value: invoiceOption.referenceId1 },
                        { field: 'ReferenceId2', value: invoiceOption.referenceId2 },
                        { field: 'ReferenceDateTimeUtc', value: invoiceOption.referenceDateTimeUtc },
                        { field: 'DeactivatedDateUtc', value: null },
                    ],
                    sortByFields: [{ field: 'CreatedDateUtc', direction: 'DESC' }],
                    offset: 0, limit: 1, includeTotalCount: false,
                })
                    .then(({ data }) => {
                        let invoiceDocument = data.result && data.result.length > 0 ? data.result[0] : null;

                        if (!invoiceDocument || reprint) {
                            if (isGeneratingPrintCallback) {
                                isGeneratingPrintCallback(true);
                            }

                            api.PrintedDocuments.print(invoiceOption)
                                .then(({ data }) => {
                                    api.PrintedDocuments.get(data.id)
                                        .then(({ data: printedDocumentData }) => {
                                            if (isGeneratingPrintCallback) {
                                                isGeneratingPrintCallback(false);
                                            }
                                            invoiceDocument = printedDocumentData;
                                            resolve(invoiceDocument);
                                        })
                                })
                        } else {
                            resolve(invoiceDocument);
                        }
                    })
            }
        }
    })
}

export const getLatestInvoiceQuoteAndPaymentReceiptBatch = (purchases, isGeneratingPrintCallback) => {
    return new Promise((resolve) => {
        if (purchases && purchases.length > 0) {
            const printRequests = [];

            for (let si = 0; si < purchases.length; si++) {
                const purchase = purchases[si];

                if (purchase.payments && purchase.payments.length > 0) {
                    const paymentReceiptOption = {
                        documentType: 'PaymentReceipt',
                        customerId: purchase.customer.id,
                        referenceId1: purchase.id,
                        referenceId2: purchase.transactions[purchase.transactions.length - 1].id,
                        referenceId3: purchase.lastPayment ? purchase.lastPayment.id : null,
                        referenceDateTimeUtc: purchase.lastPayment ? moment(purchase.lastPaymentDate).utc() : null,
                    };
                    printRequests.push(api.PrintedDocuments.print(paymentReceiptOption));
                }
                else if (purchase.isQuote) {
                    const invoiceOption = {
                        documentType: 'Quote',
                        customerId: purchase.customer ? purchase.customer.id : null,
                        referenceId1: purchase.id,
                        referenceId2: purchase.transactions[purchase.transactions.length - 1].id,
                        referenceDateTimeUtc: purchase.lastAnyUpdatedDateUtc,
                    };
                    printRequests.push(api.PrintedDocuments.print(invoiceOption));
                }
                else {
                    const invoiceOption = {
                        documentType: 'Invoice',
                        customerId: purchase.customer ? purchase.customer.id : null,
                        referenceId1: purchase.id,
                        referenceId2: purchase.transactions[purchase.transactions.length - 1].id,
                        referenceDateTimeUtc: purchase.lastAnyUpdatedDateUtc,
                    };
                    printRequests.push(api.PrintedDocuments.print(invoiceOption));
                }
            }

            if (isGeneratingPrintCallback) {
                isGeneratingPrintCallback(true);
            }

            Promise.all(printRequests)
                .then((searchResponses) => {
                    const documentRequests = searchResponses.map(sr => { return api.PrintedDocuments.get(sr.data.id) });

                    Promise.all(documentRequests)
                        .then((documentResponses) => {
                            resolve(documentResponses.map(dr => { return dr.data }));
                        })
                })
                .finally(() => {
                    if (isGeneratingPrintCallback) {
                        isGeneratingPrintCallback(false);
                    }
                })
        }
    })
}

export const renderPurchaseServiceReferenceTypes = (purchase, onClick) => {
    if (!purchase) return;

    const services = purchase.items.filter(i => i.type === 'Service');

    if (services && services.length > 0) {
        const referenceTypes = [...new Set(services.map(s => { return s.referenceType }))];

        return referenceTypes && referenceTypes.length > 0 ?
            referenceTypes.map((r, ri) => {
                return <div
                    key={`service_reference_type_${ri}`}
                    className={'d-inline-block' + (purchase.referenceId ? ' cursor-pointer' : '')}
                    onClick={onClick}
                >
                    {bh.renderPurchaseReferenceType(r, 'p-1 mr-1')}
                </div>
            }) : null
    }

}

export const renderPurchaseProductReferenceTypes = (purchase, onClick) => {
    if (!purchase) return;

    const products = purchase.items.filter(i => i.type === 'Product' || i.type === 'Custom');

    if (products && products.length > 0) {
        const referenceTypes = [...new Set(products.map(s => { return s.referenceType }))];

        for (let i = 0; i < products.length; i++) {
            for (let j = 0; j < products[i].items.length; j++) {
                if (!referenceTypes.some(r => r === products[i].items[j].referenceType)) {
                    referenceTypes.push(products[i].items[j].referenceType);
                }
            }
        }

        return referenceTypes && referenceTypes.length > 0 ?
            referenceTypes.map((r, ri) => {
                return <div
                    key={`product_reference_type_${ri}`}
                    className={'d-inline-block cursor-pointer'}
                    onClick={onClick}
                >
                    {bh.renderPurchaseReferenceType(r, 'p-1 mr-1')}
                </div>
            }) : null
    }

}

export const regenerate = (purchase, fileViewer, isGeneratingPrintCallback) => {
    getLatestInvoiceQuoteAndPaymentReceipt(purchase, isGeneratingPrintCallback, true)
        .then((printedDocument) => {
            fileViewer.initialize(() => {
                return <div className='fs-lg'>
                    {purchase.lastPayment ? 'Payment Receipt' : (purchase.isQuote ? 'Quote' : 'Invoice')}
                    <small className='d-block text-gray-700'>{purchase.lastPayment ? purchase.paymentReceiptNumber : purchase.invoiceNumber}</small>
                </div>
            }, 'printedDocument', [printedDocument], 0, (purchase.customer ? purchase.customer.id : ''))
        })
}

export const view = (purchase, fileViewer, isGeneratingPrintCallback) => {
    getLatestInvoiceQuoteAndPaymentReceipt(purchase, isGeneratingPrintCallback)
        .then((printedDocument) => {
            fileViewer.initialize(() => {
                return <div className='fs-lg'>
                    {purchase.lastPayment ? 'Payment Receipt' : (purchase.isQuote ? 'Quote' : 'Invoice')}
                    <small className='d-block text-gray-700'>{purchase.lastPayment ? purchase.paymentReceiptNumber : purchase.invoiceNumber}</small>
                </div>
            }, 'printedDocument', [printedDocument], 0, (purchase.customer ? purchase.customer.id : ''))
        })
}

export const download = (purchase, isGeneratingPrintCallback, downloadCallback) => {
    getLatestInvoiceQuoteAndPaymentReceipt(purchase, isGeneratingPrintCallback)
        .then((printedDocument) => {
            downloadFile('printedDocument', printedDocument.fileName, printedDocument.id, null, downloadCallback);
        })
}

export const print = (purchase, isGeneratingPrintCallback) => {
    getLatestInvoiceQuoteAndPaymentReceipt(purchase, isGeneratingPrintCallback)
        .then((printedDocument) => {
            api.PrintedDocuments.download(printedDocument.id)
                .then(({ data }) => {
                    printFile(
                        URL.createObjectURL(data),
                        printedDocument.mimeType,
                        () => {
                            return new Promise((resolve) => {
                                console.log('yay');
                                api.PrintedDocuments.sas(printedDocument.id)
                                    .then(response => {
                                        resolve(response)
                                    })
                            });
                        });
                })
        })
}

export const printBatch = (purchases, isGeneratingPrintCallback) => {
    getLatestInvoiceQuoteAndPaymentReceiptBatch(purchases, isGeneratingPrintCallback)
        .then((printedDocuments) => {
            const requests = printedDocuments.map(pd => { return api.PrintedDocuments.download(pd.id) });
            Promise.all(requests)
                .then((responses) => {
                    let index = 0;
                    const files = responses.map(r => { return URL.createObjectURL(r.data) });
                    const printNext = () => {
                        if (index >= files.length) return;

                        printJS({
                            printable: files[index],
                            type: 'pdf',
                            onPrintDialogClose: () => {
                                index++;
                                setTimeout(printNext, 500);
                            }
                        });
                    }

                    printNext();
                })
        })
}

export const email = (purchase, communication, isGeneratingPrintCallback) => {
    return new Promise((resolve) => {
        getLatestInvoiceQuoteAndPaymentReceipt(purchase, isGeneratingPrintCallback)
            .then((printedDocument) => {
                const action = purchase.payments && purchase.payments.length > 0 ? 'PaymentReceipt' : (purchase.isQuote ? 'Quote' : 'Invoice');

                communication.initialize(
                    'email',
                    action,
                    purchase.id,
                    [printedDocument],
                    purchase.customer.id,
                    purchase.customer.defaultEmailAddressContact,
                )
                    .then(() => {
                        resolve();
                    })
            })
    })
}

export const emailBatch = (purchases, communication, isGeneratingPrintCallback) => {
    return new Promise((resolve) => {
        getLatestInvoiceQuoteAndPaymentReceiptBatch(purchases, isGeneratingPrintCallback)
            .then((printedDocuments) => {
                if (printedDocuments && printedDocuments.length > 0) {
                    let index = 0;
                    const sendNext = () => {
                        if (index >= printedDocuments.length) {
                            resolve();
                        }
                        else {
                            const purchase = purchases.find(p => p.id === printedDocuments[index].referenceId1);
                            const action = printedDocuments[index].type;

                            if (purchase.customer.defaultEmailAddressContact) {
                                communication.initialize(
                                    'email',
                                    action,
                                    purchase.id,
                                    [printedDocuments[index]],
                                    purchase.customer.id,
                                    purchase.customer.defaultEmailAddressContact,
                                )
                                    .then(() => {
                                        communication.send({
                                            type: 'Email',
                                            to: [{
                                                id: uuid(),
                                                name: communication.defaultContact.name ? communication.defaultContact.name : 'New',
                                                value: communication.defaultContact.email ? communication.defaultContact.email : null,
                                                isNew: false,
                                            }],
                                            emailOption: {
                                                senderName: communication.selectedTemplate.senderName,
                                                subject: communication.selectedTemplate.subject,
                                                preview: communication.selectedTemplate.preview,
                                                bodyHtml: communication.selectedTemplate.body,
                                            }
                                        })
                                            .finally(() => {
                                                index++;
                                                setTimeout(sendNext, 500);
                                            })
                                    });
                            }
                        }
                    }

                    sendNext();
                }
            })
    })
}

export const getStatusFilters = () => {
    return [
        {
            text: 'Incomplete',
            value: 'Incomplete'
        },
        {
            text: 'Quote',
            value: 'Quote'
        },
        {
            text: 'Completed',
            value: 'Completed'
        },
        {
            text: 'Refunded',
            value: 'Refunded'
        },
        {
            text: 'Voided',
            value: 'Voided'
        },
        {
            text: 'Deleted',
            value: 'Deleted'
        },
    ];
}

export const getBalanceStatusFilters = () => {
    return [
        {
            text: 'Paid in full',
            value: 'Paid'
        },
        {
            text: 'Refunded',
            value: 'Refunded'
        },
        {
            text: 'Insurance pending',
            value: 'InsurancePending'
        },
        {
            text: 'Government program pending',
            value: 'GovernmentPending'
        },
        {
            text: 'Insurance / Government pending',
            value: 'InsuranceGovernmentPending'
        },
        {
            text: 'Outstanding',
            value: 'Outstanding'
        },
        {
            text: 'Overpaid',
            value: 'OverPaid'
        },
        {
            text: 'Voided',
            value: 'Voided'
        },
        {
            text: 'No charge',
            value: 'NoCharge'
        },
        {
            text: 'Uncollectible',
            value: 'Uncollectible'
        },
    ];
}

export const getNoChargeDescription = (noChargeType) => {
    if (!noChargeType) return null;

    switch (noChargeType) {
        case 'Trials':
            return `Contact Lens Trials`;

        case 'FrameWarranty':
            return `Frame Warranty`;

        case 'RxWarranty':
            return `Rx Warranty`;

        case 'FriendsFamily':
            return `Friends & Family`;

        case 'Courtesy':
            return `Courtesy Jobs`;

        default:
            return 'No Charge';
    }
}

export const getInvoiceDateFilters = () => {
    return dg.getDefaultLastDateFilters('InvoiceDate');
}

export const getQuoteExpiryDateFilters = () => {
    return dg.getDefaultLastDateFilters('QuoteExpiryDate');
}

export const getLastPaymentFilters = () => {
    return dg.getDefaultLastDateFilters('LastPaymentDate');
}

export const getLastTransactionDateFilters = () => {
    return dg.getDefaultLastDateFilters('LastTransactionDate');
}

export const getSettlementDateFilters = () => {
    return dg.getDefaultLastDateFilters('SettlementDate');
}

export const getQuickFilterDescription = (type) => {
    if (type) {
        switch (type) {
            case 'TodayInvoices':
                return `Today invoices`;

            case 'YesterdayInvoices':
                return `Yesterday invoices`;

            case 'ThisWeekInvoices':
                return `This week invoices`;

            case 'ThisMonthInvoices':
                return `This month invoices`;

            case 'LastWeekInvoices':
                return `Last week invoices`;

            case 'LastMonthInvoices':
                return `Last month invoices`;

            case 'MtdInvoices':
                return 'Month-to-Date invoices';

            case 'YtdInvoices':
                return 'Year-to-Date invoices';

            case 'LastYearInvoices':
                return `${moment().add(-1, 'years').format('YYYY')} invoices`;

            case 'OutstandingInvoices':
                return `Outstanding invoices`;

            case 'OverpaidInvoices':
                return `Overpaid invoices`;

            case 'UncollectibleInvoices':
                return `Uncollectible invoices`;

            case 'InsurancePendingInvoices':
                return `Insurance pending invoices`;

            case 'GovernmentProgramPendingInvoices':
                return `Government program pending invoices`;

            case 'RecentRefundedInvoices':
                return `Recently refunded invoices`;

            case 'RecentVoidedInvoices':
                return `Recently voided invoices`;

            case 'RecentNoChargeInvoices':
                return `Recently no charge invoices`;

            case 'ThisMonthSettledInvoices':
                return `This month settled invoices`;

            case 'LastMonthSettledInvoices':
                return `Last month settled invoices`;

            case 'MtdSettledInvoices':
                return 'Month-to-Date settled invoices';

            case 'YtdSettledInvoices':
                return 'Year-to-Date settled invoices';

            case 'LastYearSettledInvoices':
                return `${moment().add(-1, 'years').format('YYYY')} settled invoices`;

            case 'ActiveQuotes':
                return `Active quotes`;

            case 'RecentlyExpiredQuotes':
                return `Recently expired quotes`;

            default:
                return null;
        }
    }
    return null;
}

export const getQuickFilterDefinition = (type) => {
    if (type) {
        const statusFilters = getStatusFilters();
        const invoiceDateFilters = getInvoiceDateFilters();
        const balanceTypeFilters = getBalanceStatusFilters();

        switch (type) {
            case 'TodayInvoices':
                return [{
                    column: 'InvoiceDate',
                    headerFilters: [invoiceDateFilters.filter(u => u.text === 'Today')[0].value],
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [statusFilters.filter(u => u.text === 'Completed')[0].value, statusFilters.filter(u => u.text === 'Refunded')[0].value, statusFilters.filter(u => u.text === 'Voided')[0].value],
                }]

            case 'YesterdayInvoices':
                return [{
                    column: 'InvoiceDate',
                    headerFilters: [invoiceDateFilters.filter(u => u.text === 'Yesterday')[0].value],
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [statusFilters.filter(u => u.text === 'Completed')[0].value, statusFilters.filter(u => u.text === 'Refunded')[0].value, statusFilters.filter(u => u.text === 'Voided')[0].value],
                }]

            case 'ThisWeekInvoices':
                return [{
                    column: 'InvoiceDate',
                    filter: {
                        operator: 'between',
                        value: [moment().startOf('week').startOf('day').toDate(), moment().endOf('week').startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [statusFilters.filter(u => u.text === 'Completed')[0].value, statusFilters.filter(u => u.text === 'Refunded')[0].value, statusFilters.filter(u => u.text === 'Voided')[0].value],
                }]

            case 'ThisMonthInvoices':
                return [{
                    column: 'InvoiceDate',
                    filter: {
                        operator: 'between',
                        value: [moment().startOf('month').startOf('day').toDate(), moment().endOf('month').startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [statusFilters.filter(u => u.text === 'Completed')[0].value, statusFilters.filter(u => u.text === 'Refunded')[0].value, statusFilters.filter(u => u.text === 'Voided')[0].value],
                }]

            case 'LastWeekInvoices':
                return [{
                    column: 'InvoiceDate',
                    filter: {
                        operator: 'between',
                        value: [moment().add(-1, 'weeks').startOf('week').startOf('day').toDate(), moment().add(-1, 'weeks').endOf('week').startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [statusFilters.filter(u => u.text === 'Completed')[0].value, statusFilters.filter(u => u.text === 'Refunded')[0].value, statusFilters.filter(u => u.text === 'Voided')[0].value],
                }]

            case 'LastMonthInvoices':
                return [{
                    column: 'InvoiceDate',
                    filter: {
                        operator: 'between',
                        value: [moment().add(-1, 'months').startOf('month').startOf('day').toDate(), moment().add(-1, 'months').endOf('month').startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [statusFilters.filter(u => u.text === 'Completed')[0].value, statusFilters.filter(u => u.text === 'Refunded')[0].value, statusFilters.filter(u => u.text === 'Voided')[0].value],
                }]

            case 'MtdInvoices':
                return [{
                    column: 'InvoiceDate',
                    filter: {
                        operator: 'between',
                        value: [moment().startOf('month').startOf('day').toDate(), moment().startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [statusFilters.filter(u => u.text === 'Completed')[0].value, statusFilters.filter(u => u.text === 'Refunded')[0].value, statusFilters.filter(u => u.text === 'Voided')[0].value],
                }]

            case 'YtdInvoices':
                return [{
                    column: 'InvoiceDate',
                    filter: {
                        operator: 'between',
                        value: [moment().startOf('year').startOf('day').toDate(), moment().startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [statusFilters.filter(u => u.text === 'Completed')[0].value, statusFilters.filter(u => u.text === 'Refunded')[0].value, statusFilters.filter(u => u.text === 'Voided')[0].value],
                }]

            case 'LastYearInvoices':
                return [{
                    column: 'InvoiceDate',
                    filter: {
                        operator: 'between',
                        value: [moment().add(-1, 'years').startOf('year').startOf('day').toDate(), moment().add(-1, 'years').endOf('year').startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [statusFilters.filter(u => u.text === 'Completed')[0].value, statusFilters.filter(u => u.text === 'Refunded')[0].value, statusFilters.filter(u => u.text === 'Voided')[0].value],
                }]

            case 'OutstandingInvoices':
                return [{
                    column: 'InvoiceDate',
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [statusFilters.filter(u => u.text === 'Completed')[0].value],
                }, {
                    column: 'BalanceType',
                    headerFilters: [balanceTypeFilters.filter(u => u.text === 'Outstanding')[0].value],
                }]

            case 'OverpaidInvoices':
                return [{
                    column: 'InvoiceDate',
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [statusFilters.filter(u => u.text === 'Completed')[0].value],
                }, {
                    column: 'BalanceType',
                    headerFilters: [balanceTypeFilters.filter(u => u.text === 'Overpaid')[0].value],
                }]

            case 'UncollectibleInvoices':
                return [{
                    column: 'InvoiceDate',
                    sortOrder: 'asc'
                }, {
                    column: 'BalanceType',
                    headerFilters: [balanceTypeFilters.filter(u => u.text === 'Uncollectible')[0].value],
                }]

            case 'InsurancePendingInvoices':
                return [{
                    column: 'InvoiceDate',
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [statusFilters.filter(u => u.text === 'Completed')[0].value],
                }, {
                    column: 'BalanceType',
                    headerFilters: [balanceTypeFilters.filter(u => u.text === 'Insurance pending')[0].value, balanceTypeFilters.filter(u => u.text === 'Insurance / Government pending')[0].value],
                }]

            case 'GovernmentProgramPendingInvoices':
                return [{
                    column: 'InvoiceDate',
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [statusFilters.filter(u => u.text === 'Completed')[0].value],
                }, {
                    column: 'BalanceType',
                    headerFilters: [balanceTypeFilters.filter(u => u.text === 'Government program pending')[0].value, balanceTypeFilters.filter(u => u.text === 'Insurance / Government pending')[0].value],
                }]

            case 'RecentRefundedInvoices':
                return [{
                    column: 'InvoiceDate',
                    headerFilters: [invoiceDateFilters.filter(u => u.text === 'Last 30 days')[0].value],
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [statusFilters.filter(u => u.text === 'Refunded')[0].value],
                }]

            case 'RecentVoidedInvoices':
                return [{
                    column: 'InvoiceDate',
                    headerFilters: [invoiceDateFilters.filter(u => u.text === 'Last 30 days')[0].value],
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [statusFilters.filter(u => u.text === 'Voided')[0].value],
                }]

            case 'RecentNoChargeInvoices':
                return [{
                    column: 'InvoiceDate',
                    headerFilters: [invoiceDateFilters.filter(u => u.text === 'Last 30 days')[0].value],
                    sortOrder: 'asc'
                }, {
                    column: 'BalanceType',
                    headerFilters: [balanceTypeFilters.filter(u => u.text === 'No charge')[0].value],
                }]

            case 'ThisMonthSettledInvoices':
                return [{
                    column: 'SettlementDate',
                    filter: {
                        operator: 'between',
                        value: [moment().startOf('month').startOf('day').toDate(), moment().endOf('month').startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [statusFilters.filter(u => u.text === 'Completed')[0].value, statusFilters.filter(u => u.text === 'Refunded')[0].value],
                }]

            case 'LastMonthSettledInvoices':
                return [{
                    column: 'SettlementDate',
                    filter: {
                        operator: 'between',
                        value: [moment().add(-1, 'months').startOf('month').startOf('day').toDate(), moment().add(-1, 'months').endOf('month').startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [statusFilters.filter(u => u.text === 'Completed')[0].value, statusFilters.filter(u => u.text === 'Refunded')[0].value],
                }]

            case 'MtdSettledInvoices':
                return [{
                    column: 'SettlementDate',
                    filter: {
                        operator: 'between',
                        value: [moment().startOf('month').startOf('day').toDate(), moment().startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [statusFilters.filter(u => u.text === 'Completed')[0].value, statusFilters.filter(u => u.text === 'Refunded')[0].value, statusFilters.filter(u => u.text === 'Voided')[0].value],
                }]

            case 'YtdSettledInvoices':
                return [{
                    column: 'SettlementDate',
                    filter: {
                        operator: 'between',
                        value: [moment().startOf('year').startOf('day').toDate(), moment().startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [statusFilters.filter(u => u.text === 'Completed')[0].value, statusFilters.filter(u => u.text === 'Refunded')[0].value, statusFilters.filter(u => u.text === 'Voided')[0].value],
                }]

            case 'LastYearSettledInvoices':
                return [{
                    column: 'SettlementDate',
                    filter: {
                        operator: 'between',
                        value: [moment().add(-1, 'years').startOf('year').startOf('day').toDate(), moment().add(-1, 'years').endOf('year').startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [statusFilters.filter(u => u.text === 'Completed')[0].value, statusFilters.filter(u => u.text === 'Refunded')[0].value, statusFilters.filter(u => u.text === 'Voided')[0].value],
                }]

            case 'ActiveQuotes':
                return [{
                    column: 'CreatedDateUtc',
                    sortOrder: 'desc'
                }, {
                    column: 'QuoteExpiryDate',
                    filter: {
                        operator: '>=',
                        value: moment().startOf('day').toDate(),
                    }
                }, {
                    column: 'Status',
                    headerFilters: [statusFilters.filter(u => u.text === 'Quote')[0].value],
                }]

            case 'RecentlyExpiredQuotes':
                return [{
                    column: 'QuoteExpiryDate',
                    filter: {
                        operator: 'between',
                        value: [moment().add(-1, 'months').startOf('month').startOf('day').toDate(), moment().add(-1, 'days').startOf('day').toDate()],
                    },
                    sortOrder: 'desc'
                }, {
                    column: 'Status',
                    headerFilters: [statusFilters.filter(u => u.text === 'Quote')[0].value],
                }]

            default:
                return;
        }
    }
}

export const toBalanceTypeIcon = (balanceType, className) => {
    if (balanceType) {
        switch (balanceType.toLowerCase()) {
            case 'collected':
                return <i className={'fal fa-money-check-alt text-success-800 text-center' + (className ? ` ${className}` : '')} style={{ minWidth: '35px' }}></i>;

            case 'InsuranceGovernmentPending':
                return <i className={'fal fa-envelope-open-dollar text-center' + (className ? ` ${className}` : '')} style={{ color: '#009e9c', minWidth: '35px' }}></i>;

            case 'insurancepending':
                return <i className={'fal fa-envelope-open-dollar text-center' + (className ? ` ${className}` : '')} style={{ color: '#009e9c', minWidth: '35px' }}></i>;

            case 'governmentpending':
                return <i className={'fal fa-envelope-open-dollar text-center' + (className ? ` ${className}` : '')} style={{ color: '#009e9c', minWidth: '35px' }}></i>;

            case 'refunded':
                return <i className={'fal fa-money-check-alt text-warning-800 text-center' + (className ? ` ${className}` : '')} style={{ minWidth: '35px' }}></i>;

            case 'uncollectible':
                return <i className={'fal fa-times-square text-danger text-center' + (className ? ` ${className}` : '')} style={{ minWidth: '35px' }}></i>;

            default:
                break;
        }
    }

    return <i className={'fal fa-question-square text-center' + (className ? ` ${className}` : '')} style={{ color: '#f5af14', minWidth: '35px' }}></i>;
}

export const getPendingPaymentDescription = (payment) => {
    if (payment) {
        switch (payment.paymentMethod.toLowerCase()) {
            case 'privateinsurance':
                return 'Insurance pending';

            case 'governmentprogram':
                const description = 'Government program pending';
                return description;

            default:
                break;
        }
    }
}

export const getBalanceTypeDescription = (balanceType) => {
    if (balanceType) {
        switch (balanceType.toLowerCase()) {
            case 'collected':
                return 'Collected';

            case 'insurancepending':
                return 'Insurance pending';

            case 'governmentpending':
                return 'Government program pending';

            case 'InsuranceGovernmentPending':
                return 'Insurance / Government pending';

            case 'refunded':
                return 'Refunded';

            case 'uncollectible':
                return 'Uncollectible';

            default:
                break;
        }
    }
}

export const getBalanceTypeTextColor = (balanceType) => {
    if (balanceType) {
        switch (balanceType.toLowerCase()) {
            case 'paid':
            case 'InsuranceGovernmentPending':
            case 'insurancepending':
            case 'governmentpending':
            case 'nocharge':
            case 'voided':
                return 'text-primary';

            case 'outstanding':
            case 'uncollectible':
                return 'text-danger';

            case 'overpaid':
                return 'text-warning-800';

            default:
                break;
        }
    }

    return 'text-primary';
}

export const getPaymentMethodDescription = (paymentMethod) => {
    if (!paymentMethod || !paymentMethod.key) return '';
    return fn.getMethodDescription(paymentMethod.key);
}

export const getRefundMethodDescription = (refundMethod) => {
    if (!refundMethod || !refundMethod.key) return '';
    return fn.getMethodDescription(refundMethod.key);
}

export const hasVoidAccess = (purchase) => {
    const hasAccess = ach.check(AccessTypes.ALLOW_PURCHASE_VOID);
    const hasSameDayAccess = purchase && purchase.createdDateUtc && ach.check(AccessTypes.ALLOW_PURCHASE_VOID_SAME_DAY) && moment.utc(purchase.createdDateUtc).local().startOf('day').isSame(moment().startOf('day'));
    return hasAccess || hasSameDayAccess;
}

export const hasRefundAccess = (purchase) => {
    const hasAccess = ach.check(AccessTypes.ALLOW_PURCHASE_REFUND);
    const hasSameDayAccess = purchase && purchase.createdDateUtc && ach.check(AccessTypes.ALLOW_PURCHASE_REFUND_SAME_DAY) && moment.utc(purchase.createdDateUtc).local().startOf('day').isSame(moment().startOf('day'));
    return hasAccess || hasSameDayAccess;
}

export const hasNoChargeAccess = (purchase) => {
    const hasAccess = ach.check(AccessTypes.ALLOW_PURCHASE_NO_CHARGE);
    const hasSameDayAccess = purchase && purchase.createdDateUtc && ach.check(AccessTypes.ALLOW_PURCHASE_NO_CHARGE_SAME_DAY) && moment.utc(purchase.createdDateUtc).local().startOf('day').isSame(moment().startOf('day'));
    return hasAccess || hasSameDayAccess;
}

export const hasUncollectibleAccess = () => {
    return ach.check(AccessTypes.ALLOW_PURCHASE_UNCOLLECTIBLE);
}

export const hasBackdateAccess = () => {
    return ach.check(AccessTypes.ALLOW_PURCHASE_BACKDATE);
}

export const hasDeletePaymentAccess = (purchase) => {
    const hasAccess = ach.check(AccessTypes.ALLOW_PAYMENT_DELETE);
    const hasSameDayAccess = purchase && purchase.createdDateUtc && ach.check(AccessTypes.ALLOW_PAYMENT_DELETE_SAME_DAY) && moment.utc(purchase.createdDateUtc).local().startOf('day').isSame(moment().startOf('day'));
    return hasAccess || hasSameDayAccess;
}

export const hasBackdatePaymentAccess = () => {
    return ach.check(AccessTypes.ALLOW_PAYMENT_BACKDATE);
}